import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "../redux/hooks";
import {
  selectCurrentAccessToken,
  selectCurrentRefreshToken,
  setCredentials,
  tokenExpTime,
  selectTimeoutEnabled,
  selectTimeoutMinutes,
} from "../redux/auth/authSlice";
import { useRefreshSessionMutation } from "../redux/auth/authApiSlice";
import useAuth from "./useAuth";

interface UseIdleTimeoutReturn {
  showWarning: boolean;
  remainingTime: number;
  resetTimer: () => void;
  handleLogout: () => void;
}

const WARNING_TIME_SEC = 30; // 30 seconds warning before session expiry

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

const useIdleTimeout = (): UseIdleTimeoutReturn => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { logout } = useAuth();

  const jwtToken = useAppSelector(selectCurrentAccessToken);
  const refreshTokenValue = useAppSelector(selectCurrentRefreshToken);
  const isSessionTimeoutEnabled = useAppSelector(selectTimeoutEnabled);
  const expiryTimeMin = useAppSelector(selectTimeoutMinutes);
  const exp = useAppSelector(tokenExpTime);

  const expiryTimeSec = expiryTimeMin * 60;
  const warningTimeSec = WARNING_TIME_SEC; // fixed 30 seconds warning

  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(warningTimeSec);

  const [refreshSessionApi] = useRefreshSessionMutation();

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasLoggedOutRef = useRef(false);
  const showWarningRef = useRef(false);
  const isRefreshingRef = useRef(false);

  // Keep refs in sync with latest token values to avoid stale closures
  const jwtTokenRef = useRef(jwtToken);
  const refreshTokenRef = useRef(refreshTokenValue);

  useEffect(() => {
    jwtTokenRef.current = jwtToken;
    refreshTokenRef.current = refreshTokenValue;
  }, [jwtToken, refreshTokenValue]);

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (logoutTimerRef.current) {
      clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const handleLogout = useCallback(async () => {
    if (hasLoggedOutRef.current) return;
    hasLoggedOutRef.current = true;

    clearAllTimers();
    setShowWarning(false);
    showWarningRef.current = false;

    await logout({ isSessionTimeout: true });
    navigate("/login", { replace: true, state: { sessionExpired: true } });
  }, [clearAllTimers, logout, navigate]);

  const startCountdown = useCallback((countdownSec: number) => {
    setShowWarning(true);
    showWarningRef.current = true;
    setRemainingTime(Math.round(countdownSec));

    const logoutTime = Date.now() + countdownSec * 1000;

    countdownIntervalRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.round((logoutTime - Date.now()) / 1000));
      setRemainingTime(remaining);
      if (remaining <= 0 && countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    }, 1000);

    logoutTimerRef.current = setTimeout(() => {
      handleLogout();
    }, countdownSec * 1000);
  }, [handleLogout]);

  const startIdleTimer = useCallback(() => {
    clearAllTimers();
    hasLoggedOutRef.current = false;
    setShowWarning(false);
    showWarningRef.current = false;
    setRemainingTime(warningTimeSec);

    const timeUntilWarningSec = expiryTimeSec - warningTimeSec;

    if (timeUntilWarningSec <= 0) {
      startCountdown(expiryTimeSec);
      return;
    }

    warningTimerRef.current = setTimeout(() => {
      startCountdown(warningTimeSec);
    }, timeUntilWarningSec * 1000);
  }, [clearAllTimers, expiryTimeSec, warningTimeSec, startCountdown]);

  // "Stay Logged In" - refresh the token and restart the idle timer
  const resetTimer = useCallback(async () => {
    if (hasLoggedOutRef.current) return;

    clearAllTimers();
    setShowWarning(false);
    showWarningRef.current = false;
    setRemainingTime(warningTimeSec);

    try {
      // Use refs to always get the latest token values (avoids stale closure)
      const response: any = await refreshSessionApi({
        accessToken: jwtTokenRef.current,
        refreshToken: refreshTokenRef.current,
      }).unwrap();
      dispatch(setCredentials(response.result));
    } catch {
      // If refresh fails, force logout
      handleLogout();
    }
  }, [clearAllTimers, warningTimeSec, refreshSessionApi, dispatch, handleLogout]);

  // Start idle timer on mount and whenever token changes
  useEffect(() => {
    if (!jwtToken || !isSessionTimeoutEnabled) return;

    startIdleTimer();

    return () => {
      clearAllTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken, expiryTimeSec, warningTimeSec, isSessionTimeoutEnabled]);

  // Proactively refresh the token when user is active and token is near expiry
  const proactiveRefresh = useCallback(async () => {
    if (isRefreshingRef.current || hasLoggedOutRef.current) return;

    // Check if token will expire within the next 5 minutes
    const isNearExpiry = exp && exp < (Date.now() / 1000 + 60 * 5);
    if (!isNearExpiry) return;

    isRefreshingRef.current = true;
    try {
      // Use refs to always get the latest token values (avoids stale closure)
      const response: any = await refreshSessionApi({
        accessToken: jwtTokenRef.current,
        refreshToken: refreshTokenRef.current,
      }).unwrap();
      dispatch(setCredentials(response.result));
    } catch {
      // Refresh failed — don't force logout here, let the idle timer handle it
    } finally {
      isRefreshingRef.current = false;
    }
  }, [exp, refreshSessionApi, dispatch]);

  // Listen for user activity and reset idle timer (only when warning is NOT showing)
  useEffect(() => {
    if (!jwtToken || !isSessionTimeoutEnabled) return;

    const handleUserActivity = () => {
      // Don't reset if warning dialog is already showing or user has logged out
      if (showWarningRef.current || hasLoggedOutRef.current) return;
      startIdleTimer();
      // Proactively refresh token if it's near expiry while user is active
      proactiveRefresh();
    };

    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleUserActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleUserActivity);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwtToken, startIdleTimer, proactiveRefresh, isSessionTimeoutEnabled]);

  return {
    showWarning,
    remainingTime,
    resetTimer,
    handleLogout,
  };
};

export default useIdleTimeout;
